import { booleanAttribute, Directive, ElementRef, Input, OnChanges, OnInit } from '@angular/core';

/**
 * Appends a red asterisk (*) to a label element to visually indicate a required field.
 *
 * Usage — always required:
 *   <label inputRequired for="startDate" class="form-label">Start Date</label>
 *
 * Usage — conditionally required (e.g., only required for teaching positions):
 *   <label [inputRequired]="isTeachingPosition" for="periodsTaught" class="form-label">Periods Taught</label>
 *
 * When [inputRequired]="false" the asterisk is removed; any other value (including the
 * bare attribute with no binding) results in the asterisk being shown.
 */
@Directive({
  selector: 'label[inputRequired]',
  standalone: false,
})
export class RequiredInputDirective implements OnInit, OnChanges {
  @Input({ alias: 'inputRequired', transform: booleanAttribute }) isRequired = true;

  private asteriskSpan: HTMLSpanElement | null = null;
  private initialized = false;

  constructor(private readonly el: ElementRef<HTMLLabelElement>) {}

  ngOnInit(): void {
    this.initialized = true;
    this.updateAsterisk();
  }

  ngOnChanges(): void {
    if (this.initialized) {
      this.updateAsterisk();
    }
  }

  private updateAsterisk(): void {
    const shouldShow = this.isRequired;

    if (shouldShow && !this.asteriskSpan) {
      this.asteriskSpan = document.createElement('span');
      this.asteriskSpan.className = 'text-danger ms-1';
      this.asteriskSpan.setAttribute('aria-hidden', 'true');
      this.asteriskSpan.setAttribute('title', 'Required field');
      this.asteriskSpan.textContent = '*';
      this.el.nativeElement.appendChild(this.asteriskSpan);
    } else if (!shouldShow && this.asteriskSpan) {
      this.asteriskSpan.remove();
      this.asteriskSpan = null;
    }
  }
}
